<script setup lang="ts">
import { computed, nextTick, reactive, ref } from 'vue'
import { renderCopy, consultantTermVars } from '@/copy'
import { env } from '@/utils'
import { Ruleset } from '@/objects'
import RulesetTable from '@/Components/RulesetTable.vue'

/**
 * Login-time onboarding flow shown before the session (see Play.vue). It is a linear sequence of
 * pages: each part's rich-text instructions, then one page per comprehension question. On a
 * question page "Avanti" stays disabled until the participant picks the correct option; wrong
 * answers only block progress — nothing is recorded and there is no penalty (the correct answers
 * ship in the config prop, so gating is entirely client-side). Finishing the last page emits
 * `done`, handing control back to the game board.
 */
type Question = {
    text: string
    option_1: string
    option_2: string
    option_3: string
    correct: number
    // Optional payoff table shown with the question, so it can be worked out from the numbers
    // rather than from memory: 'original' (the Part 1 table) or 'alternative'. Unset = none.
    table?: 'original' | 'alternative' | null
}
type Part = {
    instructions: string
    audience?: string
    questions: Question[]
}
type Step =
    | { type: 'instructions'; part: number }
    | { type: 'question'; part: number; q: number }

// `parts` is already filtered for the treatment by the caller; `chatType` only drives the
// `consulente` merge-tag substitution in the instructions HTML. `rulesets` feeds the optional
// per-question payoff table (see studyRulesets); omit it and questions simply show no table.
const props = defineProps<{
    parts: Part[]
    chatType?: string | null
    rulesets?: { original: Ruleset | null, alternative: Ruleset | null }
}>()
const emit = defineEmits<{ (e: 'done'): void }>()

const termVars = computed(() => consultantTermVars(props.chatType))

// Flatten the parts into an ordered list of one-per-page steps:
// [p1 instructions, p1 q1, p1 q2, p2 instructions, p2 q1, ...].
const steps = computed<Step[]>(() => {
    const out: Step[] = []
    props.parts.forEach((part, p) => {
        out.push({ type: 'instructions', part: p })
        ;(part.questions ?? []).forEach((_, q) => out.push({ type: 'question', part: p, q }))
    })
    return out
})

const stepIdx = ref(0)
const scrollEl = ref<HTMLElement | null>(null)

// selected[partIdx][questionIdx] = chosen 1-based option, or null if untouched.
const selected = reactive<(number | null)[][]>(
    props.parts.map((part) => (part.questions ?? []).map(() => null)),
)

const current = computed(() => steps.value[stepIdx.value])
const isLast = computed(() => stepIdx.value === steps.value.length - 1)

// The question shown on the current page (null on an instructions page).
const currentQuestion = computed<Question | null>(() =>
    current.value.type === 'question' ? props.parts[current.value.part].questions[current.value.q] : null,
)

// The table this question asks about, if the researcher attached one to it.
const questionRuleset = computed<Ruleset | null>(() => {
    const which = currentQuestion.value?.table

    if (!which) {
        return null
    }

    return (which === 'alternative' ? props.rulesets?.alternative : props.rulesets?.original) ?? null
})

function optionsOf(q: Question): string[] {
    return [q.option_1, q.option_2, q.option_3]
}

function isCorrect(pIdx: number, qIdx: number): boolean {
    return selected[pIdx][qIdx] === props.parts[pIdx].questions[qIdx].correct
}

// Only surface the "wrong answer" hint once the participant has actually picked something.
function isWrong(pIdx: number, qIdx: number): boolean {
    const chosen = selected[pIdx][qIdx]
    return chosen !== null && chosen !== props.parts[pIdx].questions[qIdx].correct
}

// Instructions pages are always passable; a question page requires the correct option.
const canAdvance = computed(() => {
    if (env().DEV) {
        return true
    }

    const s = current.value
    return s.type === 'instructions' || isCorrect(s.part, s.q)
})

function scrollTop(): void {
    nextTick(() => scrollEl.value?.scrollTo({ top: 0 }))
}

function back(): void {
    if (stepIdx.value > 0) {
        stepIdx.value--
        scrollTop()
    }
}

/**
 * Skip to the end (DEV only)
 */
function skip() {
    if (!env().DEV) {
        return
    }

    emit('done')
}

function next(): void {
    if (!canAdvance.value) {
        return
    }
    if (isLast.value) {
        emit('done')
        return
    }
    stepIdx.value++
    scrollTop()
}
</script>

<template>
    <!-- A reading column as wide as the "Istruzioni" recap modal (w-240), so the same copy keeps
         the same line breaks whether it is read here or reopened later. The cap only bites on
         desktop; below ~1000px the width comes from the gutter, which stays at the phone-friendly
         `px-5` — widening the column must not eat into the margin on a small screen. -->
    <div ref="scrollEl" data-testid="onboarding" class="fixed inset-0 z-50 bg-(--bg-color) col items-center overflow-y-auto px-5 py-7">
        <div class="col w-full max-w-240 gap-6 text-md">
            <!-- Step indicator -->
            <div class="row items-center justify-center flex-wrap gap-1.5">
                <span v-for="(_, i) in steps" :key="i"
                    class="size-2 rounded-full"
                    :class="i === stepIdx ? 'bg-blue' : (i < stepIdx ? 'bg-blue-300' : 'bg-gray-300')"></span>
            </div>

            <!-- Instructions page -->
            <div v-if="current.type === 'instructions'" class="copy-html" v-html="renderCopy(parts[current.part].instructions, termVars)"></div>

            <!-- Question page -->
            <div v-else-if="currentQuestion" class="col gap-3">
                <span class="text-sm text-gray-600">Parte {{ current.part + 1 }}</span>
                <p class="font-semibold">{{ currentQuestion.text }}</p>
                <!-- The table the question is about, when one is attached to it: reading the
                     answer off the numbers beats remembering them from the page before. -->
                <div v-if="questionRuleset" class="col items-start gap-2 py-1">
                    <span class="text-sm font-bold">TABELLA {{ currentQuestion.table === 'alternative' ? 'ALTERNATIVA' : 'ORIGINALE' }}</span>
                    <RulesetTable :ruleset="questionRuleset" />
                </div>
                <label v-for="(opt, oIdx) in optionsOf(currentQuestion)" :key="oIdx"
                    class="row items-center gap-2.5 cursor-pointer rounded-md px-3 py-2 border"
                    :class="selected[current.part][current.q] === oIdx + 1
                        ? (isCorrect(current.part, current.q) ? 'border-blue bg-blue-100' : 'border-error bg-error-light')
                        : 'border-gray-300'">
                    <input type="radio" class="accent-blue"
                        :name="`q-${current.part}-${current.q}`" :value="oIdx + 1"
                        v-model="selected[current.part][current.q]">
                    <span>{{ opt }}</span>
                </label>
                <span v-if="isWrong(current.part, current.q)" class="text-error text-sm">
                    Risposta errata. Rileggi le istruzioni e riprova.
                </span>
            </div>

            <div class="row items-center justify-between gap-3 pt-2">
                <button class="btn-secondary px-6! py-2.5!"
                    :disabled="stepIdx === 0" @click="back">
                    Indietro
                </button>
                <button class="btn-primary px-6! py-2.5!" data-testid="onboarding-next"
                    :disabled="!canAdvance" @click="next" @click.shift="skip">
                    {{ isLast ? 'Inizia' : 'Avanti' }}
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* The instructions read as a document, so their paragraphs are justified like the printed
   original. Only <p>: headings, list items and the questions keep a ragged right edge, and the
   copy's own `style="text-align:center"` captions (the payoff-table titles) still win. */
.copy-html :deep(p) {
    text-align: justify;
}
</style>
